<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useMensagensClienteStore } from '@/stores/mensagensCliente'
import { useNotificationStore } from '@/stores/notification'
import { formatToDateTimeBR } from '@/utils/date'

const store = useMensagensClienteStore()
const notificationStore = useNotificationStore()

// UI state
const searchStatus = ref('') // optional: status filter
const searchClienteNome = ref('') // optional: cliente nome filter
const searchClienteEmail = ref('') // optional: cliente email filter
const searchClienteTelefone = ref('') // optional: cliente telefone filter
const dateFrom = ref('')
const dateTo = ref('')
const perPage = ref(100)
const page = ref(1)
const dialogOpen = ref(false)
const selectedMessage = ref(null)
const observationText = ref('')
const loadingObservationRequest = ref(false)

// simple debounce for filters
let debounceTimer = null
function debounceFetch(delay = 400) {
  clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    fetchList()
  }, delay)
}

function resetFilters() {
  searchStatus.value = ''
  searchClienteNome.value = ''
  searchClienteEmail.value = ''
  searchClienteTelefone.value = ''
  dateFrom.value = ''
  dateTo.value = ''
  page.value = 1
  store.resetFilters()
}

async function fetchList() {
  await store.fetchMensagens({
    status: searchStatus.value || undefined,
    cliente_nome: searchClienteNome.value || undefined,
    cliente_email: searchClienteEmail.value || undefined,
    cliente_telefone1: searchClienteTelefone.value || undefined,
    init_created_at: dateFrom.value || undefined,
    end_created_at: dateTo.value || undefined,
    per_page: perPage.value,
    page: page.value,
  })
}

// open modal for a message
function openObservationModal(item) {
  selectedMessage.value = item
  observationText.value = ''
  dialogOpen.value = true
}

// send observation and refresh list
async function sendObservation() {
  try {
    if (!selectedMessage.value) return

    loadingObservationRequest.value = true

    const res = await store.enviarResposta(selectedMessage.value.id, observationText.value || '')

    if (res.success) {
      notificationStore.showNotification('Observação enviada com sucesso', 'success')
      dialogOpen.value = false
    } else {
      notificationStore.showNotification(res.error || 'Erro ao enviar observação', 'error')
    }
  } finally {
    loadingObservationRequest.value = false
  }
}

// watchers
watch([perPage, page], () => {
  fetchList()
})

watch(
  [searchStatus, searchClienteNome, searchClienteEmail, searchClienteTelefone, dateFrom, dateTo],
  () => {
    page.value = 1
    debounceFetch()
  },
)

onMounted(() => {
  // data inicial e fim atual
  const now = new Date()

  dateFrom.value = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    .toISOString()
    .substr(0, 10)
  dateTo.value = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    .toISOString()
    .substr(0, 10)

  fetchList()

  // listener para novas mensagens
  window.addEventListener('notifications:new-messages', fetchList)

  // garante remoção quando a página for descarregada
  window.addEventListener('beforeunload', () => {
    window.removeEventListener('notifications:new-messages', fetchList)
  })
})

// computed items
const mensagens = computed(() => store.items || [])
const loading = computed(() => store.loading)
const meta = computed(() => store.meta || { current_page: 1, last_page: 1, total: 0 })
</script>

<template>
  <div>
    <v-row class="mb-3">
      <v-col cols="12" class="d-flex align-center">
        <h1 class="text-h4">Mensagens de Clientes</h1>
        <v-spacer></v-spacer>
      </v-col>
    </v-row>

    <v-card class="pa-3 mb-3">
      <v-row align="center">
        <v-col cols="12" md="3">
          <v-select
            v-model="searchStatus"
            :items="[
              { label: 'Todos', value: '' },
              { label: 'Resolvido', value: 'resolvido' },
              { label: 'Não lido', value: 'nao_lido' },
            ]"
            item-title="label"
            item-value="value"
            label="Status"
            density="compact"
            hide-details
            clearable
          />
        </v-col>

        <v-col cols="12" md="3">
          <v-text-field
            v-model="searchClienteNome"
            label="Nome do cliente"
            density="compact"
            hide-details
            clearable
          />
        </v-col>

        <v-col cols="12" md="3">
          <v-text-field
            v-model="searchClienteEmail"
            label="E-mail do cliente"
            density="compact"
            hide-details
            clearable
          />
        </v-col>

        <v-col cols="12" md="3">
          <v-text-field
            v-model="searchClienteTelefone"
            v-mask="'(##) #####-####'"
            label="Telefone do cliente"
            density="compact"
            hide-details
            clearable
          />
        </v-col>
      </v-row>

      <v-row align="center" class="mt-1">
        <v-col cols="12" md="3">
          <v-text-field
            v-model="dateFrom"
            type="date"
            label="Data início"
            density="compact"
            hide-details
          />
        </v-col>

        <v-col cols="12" md="3">
          <v-text-field
            v-model="dateTo"
            type="date"
            label="Data fim"
            density="compact"
            hide-details
          />
        </v-col>

        <v-col cols="12" md="6" class="d-flex align-end justify-end ga-2">
          <v-btn @click="resetFilters">Limpar</v-btn>

          <v-btn color="primary" @click="fetchList">Aplicar filtros</v-btn>
        </v-col>
      </v-row>
    </v-card>

    <v-alert v-if="store.error" type="error" class="mb-4">{{ store.error }}</v-alert>

    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
      class="mb-2"
    ></v-progress-linear>

    <v-data-table
      :headers="[
        { title: 'Situação', value: 'status' },
        { title: 'Cliente', value: 'cliente_nome' },
        { title: 'Mensagem', value: 'mensagem' },
        { title: 'Observação', value: 'observation' },
        { title: 'Criado em', value: 'created_at' },
        { title: 'Atualizado em', value: 'updated_at' },
        { title: 'Ações', value: 'actions', sortable: false },
      ]"
      :items="mensagens"
      class="custom-table"
      :items-per-page="perPage"
    >
      <template #item="{ item }">
        <tr>
          <td>
            <v-chip
              size="x-small"
              :color="item.status == 'Resolvido' ? 'success' : 'error'"
              class="ml-1"
              >{{ item.status || '-' }}</v-chip
            >
          </td>

          <td>
            <p>{{ item.cliente_nome || '-' }}</p>
            <p>{{ item.cliente_email || '-' }}</p>
            <p>{{ item.cliente_telefone1 || '-' }}</p>
          </td>
          <td>
            <div
              style="
                max-width: 360px;
                white-space: pre-wrap;
                word-break: break-word;
                overflow-wrap: anywhere;
              "
            >
              {{ item.mensagem || item.message || '-' }}
            </div>
          </td>
          <td>
            <div
              style="
                max-width: 360px;
                white-space: pre-wrap;
                word-break: break-word;
                overflow-wrap: anywhere;
              "
            >
              {{ item.observation || item.obs || '-' }}
            </div>
          </td>
          <td>{{ item?.created_at ? formatToDateTimeBR(item.created_at) : '-' }}</td>
          <td>{{ item?.updated_at ? formatToDateTimeBR(item.updated_at) : '-' }}</td>
          <td>
            <v-icon icon="mdi-comment-plus-outline" @click="openObservationModal(item)" />
          </td>
        </tr>
      </template>

      <template #bottom>
        <div class="d-flex justify-space-between align-center px-4 py-2">
          <div class="d-flex align-center">
            <span class="text-caption text-secondary mr-4">
              Total de {{ meta.total || mensagens.length }} item{{
                (meta.total || 0) > 1 ? 's' : ''
              }}
            </span>

            <v-select
              v-model="perPage"
              :items="[10, 25, 50, 100]"
              label="Itens por página"
              variant="outlined"
              dense
              hide-details
              style="width: 140px"
            />
          </div>

          <v-pagination
            v-if="meta.last_page > 1"
            :model-value="page"
            :length="meta.last_page"
            :total-visible="5"
            @update:model-value="(newPage) => (page = newPage)"
            rounded
            class="my-2"
          ></v-pagination>
        </div>
      </template>
    </v-data-table>

    <v-dialog v-model="dialogOpen" max-width="600">
      <v-card>
        <v-card-title class="text-h6"
          >Enviar Observação - Mensagem #{{ selectedMessage?.id }}</v-card-title
        >
        <v-card-text>
          <div>
            <div class="mb-2">
              <strong>Cliente:</strong>
              {{ selectedMessage?.cliente?.nome || selectedMessage?.cliente_nome || '-' }}
            </div>
            <div class="mb-2"><strong>Mensagem:</strong></div>
            <div class="mb-3" style="background: #f5f5f5; padding: 8px; border-radius: 4px">
              {{ selectedMessage?.mensagem || selectedMessage?.message || '-' }}
            </div>
            <v-textarea v-model="observationText" label="Observação" rows="4" auto-grow />
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn class="bg-blue-grey-lighten-4" text @click="dialogOpen = false">Cancelar</v-btn>
          <v-btn
            class="font-weight-bold bg-primary"
            color="white"
            :disabled="loadingObservationRequest"
            @click="sendObservation"
            >{{ loadingObservationRequest ? 'Enviando...' : 'Enviar' }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
/* ...existing styles... */
</style>
